"use client";

import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from "@tillix/ui-atoms/atoms/accordion";
import { ExternalLink } from "lucide-react";
import { useT } from "@/features/i18n/hooks/use-t";
import { tillixTermsSections } from "./tillix-terms-data";
import type { TilliXContentBlock } from "./tillix-terms-data";

function ContentBlock({
  block,
  t,
}: {
  block: TilliXContentBlock;
  t: (key: string) => string;
}) {
  switch (block.type) {
    case "paragraph":
      return (
        <p className="text-muted-foreground leading-relaxed">{t(block.key)}</p>
      );
    case "subheading":
      return <h4 className="font-semibold text-sm">{t(block.key)}</h4>;
    case "list":
      return (
        <div className="space-y-2">
          {block.introKey && (
            <p className="text-muted-foreground leading-relaxed">
              {t(block.introKey)}
            </p>
          )}
          <ul className="list-disc space-y-1 pl-5">
            {block.itemKeys.map((key) => (
              <li className="text-muted-foreground leading-relaxed" key={key}>
                {t(key)}
              </li>
            ))}
          </ul>
        </div>
      );
    case "link":
      return (
        <a
          className="inline-flex items-center gap-1.5 text-primary text-sm hover:underline"
          href={block.href}
          rel="noopener noreferrer"
          target="_blank"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          {t(block.key)}
        </a>
      );
    default:
      return null;
  }
}

export default function TilliXTermsContent() {
  const { t } = useT();

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">{t("terms.lastUpdated")}</p>
      <Accordion defaultValue={[tillixTermsSections[0].id]} multiple={true}>
        {tillixTermsSections.map((section) => (
          <AccordionItem key={section.id} value={section.id}>
            <AccordionTrigger className="text-left hover:no-underline">
              {t(section.titleKey)}
            </AccordionTrigger>
            <AccordionPanel>
              <div className="space-y-3">
                {section.content.map((block) => (
                  <ContentBlock
                    block={block}
                    key={block.type === "list" ? block.itemKeys[0] : block.key}
                    t={t}
                  />
                ))}
              </div>
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
